<template>
  <el-card class="reviewer-setting">
    <div class="setting-section">
      <div class="setting-title flex row-between m-b-22">
        {{ beforeReviewStart ? '选择评审操作方式' : '评审操作方式' }}
      </div>
      <el-form v-if="beforeReviewStart" :model="form" :rules="rules">
        <el-form-item label="评审操作方式" prop="isReviewerScore">
          <el-radio-group
            v-model="form.isReviewerScore"
            :disabled="!!form.list.length"
          >
            <el-radio
              v-for="item in onlineReviewOperationMethodDict.array"
              :key="item.value"
              :label="item.value"
            >
              <span>{{ item.label }}</span>
              <el-popover
                placement="top"
                trigger="hover"
                :content="item.tip"
                class="m-l-8"
              >
                <i slot="reference" class="el-icon-info"></i>
              </el-popover>
            </el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <el-descriptions v-else size="medium">
        <el-descriptions-item label="评审操作方式">
          <span>
            {{ reviewerMode.label }}
          </span>
          <el-popover
            placement="top"
            trigger="hover"
            :content="reviewerMode.tip"
            class="m-l-8"
          >
            <i slot="reference" class="el-icon-info"></i>
          </el-popover>
        </el-descriptions-item>
      </el-descriptions>
    </div>

    <!-- 项目负责人登记评审时，组建评审小组 -->
    <online-reviewer-registration
      v-if="isRegistrationReview"
      ref="registrationRef"
      :list="form.list"
      @openReview="onOpenReview"
      @afterChange="reloadReview"
    />

    <!-- 专家评审时，组建评审小组 -->
    <online-reviewer-standard
      v-if="isStandardReview"
      :list="form.list"
      @reloadReview="reloadReview"
      @openReview="onOpenReview"
    />
  </el-card>
</template>

<script>
import OnlineReviewerRegistration from '@/components/online-review/project/OnlineReviewerRegistration.vue'
import OnlineReviewerStandard from '@/components/online-review/project/OnlineReviewerStandard.vue'
import { requiredRule } from '@/utils/form-validate'
import { onlineReviewerModeDict } from '@/dictionary/project/online-review'
import { generatePassword } from '@/utils/utils'
import useReviewOnline from '@/components/online-review/hooks/useReviewOnline'
import { toReviewOnlineHall } from '@/utils/platform'
import { purchaseTypeDict } from '@/dictionary/project/common'
import {
  copyPurchaseReviewerFromHistoryApi,
  removePurchaseReviewerApi,
  resetPurchaseReviewerAllPasswordApi,
  resetPurchaseReviewerPasswordApi,
  setPurchaseReviewerModeApi,
  setUpPurchaseReviewerListApi,
  syncPurchaseExtractReviewerApi
} from '@/api/procure/purchase-review'
import { generateTableHtml } from '@/utils/generate-html'
import { isEqual } from 'lodash'

/**
 * 线上评审专家组建功能
 */
export default {
  name: 'OnlineReviewerSetting',

  components: { OnlineReviewerRegistration, OnlineReviewerStandard },

  inject: [
    'provideReviewId',
    'provideOrderId',
    'providePurchaseType',
    'provideReviewProject',
    'provideBeforeStart'
  ],

  provide() {
    return {
      provideSyncReviewer: this.syncReviewer,
      provideRemoveExpert: this.removeExpert,
      provideResetExpertPassword: this.resetExpertPassword,
      provideResetPrint: this.resetPrint,
      provideCopyReviewer: this.copyReviewer,
      provideSetUpReviewerManually: this.setUpReviewerManually
    }
  },

  data() {
    return {
      onlineReviewOperationMethodDict: onlineReviewerModeDict,

      form: {
        // 评审操作方式
        isReviewerScore: undefined,
        list: []
      },
      rules: {
        isReviewerScore: requiredRule('评审操作方式不能为空')
      }
    }
  },

  computed: {
    reviewId() {
      return this.provideReviewId()
    },

    orderId() {
      return this.provideOrderId()
    },

    reviewProject() {
      return this.provideReviewProject()
    },

    purchaseType() {
      return this.providePurchaseType()
    },

    beforeReviewStart() {
      return this.provideBeforeStart()
    },

    // 是否是项目经理登记评审
    isRegistrationReview() {
      return (
        this.form.isReviewerScore === onlineReviewerModeDict.PURCHASER.value
      )
    },

    isStandardReview() {
      return this.form.isReviewerScore === onlineReviewerModeDict.EXPERT.value
    },

    reviewerMode() {
      return onlineReviewerModeDict.translate(this.form.isReviewerScore) || {}
    }
  },

  watch: {
    reviewProject: {
      handler(val, oldVal) {
        if (val?.reviewId && !isEqual(val, oldVal)) {
          this.form.isReviewerScore = val.reviewInfo?.isReviewerScore
          this.form.list = val.reviewerInfoList
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    /**
     * 点击开启评审
     */
    onOpenReview() {
      if (this.isRegistrationReview) {
        this.$refs.registrationRef
          .validateTable()
          .then(() => {
            this.openReview()
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
        return
      }

      this.openReview()
    },

    /**
     * 开启评审
     */
    openReview() {
      useReviewOnline()
        .openReview({
          purchaseType: this.purchaseType,
          reviewId: this.reviewId,
          orderId: this.orderId,
          isReviewerScore: this.form.isReviewerScore
        })
        .then(() => {
          // 前往专家平台的采购人线上评审大厅
          toReviewOnlineHall({
            reviewId: this.reviewId,
            orderId: this.orderId,
            purchaseType: this.purchaseType
          })
          window.location.reload()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    reloadReview() {
      this.$emit('reloadReview')
    },

    setReviewerMode() {
      setPurchaseReviewerModeApi(this.reviewId, {
        isReviewerScore: this.form.isReviewerScore
      })
        .then(() => {
          this.reloadReview()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 同步专家抽取结果
     */
    syncReviewer() {
      syncPurchaseExtractReviewerApi(this.reviewId, {
        orderId: this.orderId,
        purchaseType: this.purchaseType
      })
        .then(() => {
          this.setReviewerMode()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 从历史项目中复制专家
     */
    copyReviewer(row) {
      return copyPurchaseReviewerFromHistoryApi(this.reviewId, {
        oldReviewId: row.reviewId
      })
        .then(() => {
          this.$message.success('导入成功')
          this.setReviewerMode()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 手动添加专家
     */
    setUpReviewerManually(form) {
      this.form.list.push({
        expertId: form.expertId,
        expertName: form.expertName,
        expertMobile: form.expertPhone,
        isPurchaseExpert: form.isPurchaseExpert
      })
      return setUpPurchaseReviewerListApi(this.reviewId, this.form.list)
        .then(() => {
          this.setReviewerMode()
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 重置当前专家密码
     */
    resetExpertPassword(row) {
      this.$systemConfirm('是否确认重置当前评委登录密码', '重置密码确认').then(
        bool => {
          if (!bool) return

          const resetPassword = generatePassword(8, 'Jxtc', {
            letters: false
          })

          resetPurchaseReviewerPasswordApi(this.reviewId, row.reviewerId, {
            password: resetPassword
          })
            .then(() => {
              this.$alert(
                `<div>请记录当前重置的登录密码以免丢失：<span class="text-primary">${resetPassword}</span></div>`,
                '登录密码确认',
                {
                  showCancelButton: false,
                  type: 'success',
                  dangerouslyUseHTMLString: true,
                  confirmButtonText: '知道了'
                }
              )
            })
            .catch(err => {
              if (err?.message) this.$message.error(err.message)
            })
        }
      )
    },

    /**
     * 移除专家
     */
    removeExpert(row) {
      this.$systemConfirm(`是否确认移除${row.expertName}评委`, '系统提示').then(
        bool => {
          if (!bool) return

          removePurchaseReviewerApi(this.reviewId, row.reviewerId)
            .then(() => {
              this.$message.success('当前评委已移除')
              this.reloadReview()
            })
            .catch(err => {
              if (err?.message) this.$message.error(err.message)
            })
        }
      )
    },

    /**
     * 一键重置并打印
     */
    resetPrint() {
      this.$systemConfirm(
        '是否确认一键重置列表评委的登录密码',
        '一键重置密码确认'
      ).then(bool => {
        if (!bool) return

        resetPurchaseReviewerAllPasswordApi(this.reviewId)
          .then(res => {
            const data = res.data || []
            const tableData = data.map(item => {
              return [
                { content: item.expertName || '' },
                { content: item.expertPhone || '' },
                { content: item.password || '' }
              ]
            })

            const htmlStr = generateTableHtml({
              pdfTitle: '',
              describeInfoTop: [],
              tableHead: {
                dynamic: [
                  { content: '专家姓名' },
                  { content: '登录账号' },
                  { content: '登录密码' }
                ]
              },
              tableData: tableData,
              needHtmlWrapper: true,
              direction: 'vertical'
            })

            const tempIframe = document.createElement('iframe')
            document.body.appendChild(tempIframe)
            tempIframe.contentDocument.write(htmlStr)
            tempIframe.contentWindow.print()
            document.body.removeChild(tempIframe)
          })
          .catch(err => {
            this.$message.error(err.message || '重置失败')
          })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.setting-section {
  .setting-title {
    font-size: 16px;
    color: $text-color;
  }
}
</style>
